import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Backdrop,
  CircularProgress,
  LinearProgress,
  Alert,
  Typography,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import { apiFetch } from "../../api";

import CsvColumnMappingStep from "./CsvColumnMappingStep";
import CsvPreviewStep from "./CsvPreviewStep";
import CsvDuplicatesDialog from "./CsvDuplicatesDialog";

const OBTENIDO_LIST = [
  "Meta Ads",
  "Messenger",
  "Instagram",
  "Facebook",
  "Contacto Directo",
  "Otro",
];

const REQUIRED_FIELDS = ["nombre", "telefono", "vehiculo_consultado"];

export default function ImportClientesCsvDialog({
  open,
  onClose,
  onImported, // callback({ insertados, saltados, total })
}) {
  const [activeStepKey, setActiveStepKey] = React.useState("file"); // 'file' | 'sheet' | 'columns' | 'preview' | 'importing' | 'result'
  const [file, setFile] = React.useState(null);
  const [obtenidoDe, setObtenidoDe] = React.useState("Meta Ads");

  // Inspección del archivo
  const [inspecting, setInspecting] = React.useState(false);
  const [inspectData, setInspectData] = React.useState(null);
  const [selectedSheet, setSelectedSheet] = React.useState(null);

  // Mapeo
  const [mapping, setMapping] = React.useState({});
  const [validationError, setValidationError] = React.useState(null);

  // Vista previa
  const [previewing, setPreviewing] = React.useState(false);
  const [previewData, setPreviewData] = React.useState(null);

  // Importación final
  const [submitting, setSubmitting] = React.useState(false);
  const submittingRef = React.useRef(false);
  const [error, setError] = React.useState(null);

  const [importResult, setImportResult] = React.useState(null);
  const [resultDialogOpen, setResultDialogOpen] = React.useState(false);

  const loading = submitting || inspecting || previewing;

  // Determinar si hay múltiples hojas para mostrar o no el paso 'Hoja'
  const hasMultipleSheets = Boolean(inspectData?.sheets && inspectData.sheets.length > 1);

  const steps = hasMultipleSheets
    ? ["Archivo", "Hoja", "Columnas", "Vista previa", "Importar", "Resultado"]
    : ["Archivo", "Columnas", "Vista previa", "Importar", "Resultado"];

  const getStepIndex = () => {
    if (activeStepKey === "file") return 0;
    if (activeStepKey === "sheet") return 1;
    if (activeStepKey === "columns") return hasMultipleSheets ? 2 : 1;
    if (activeStepKey === "preview") return hasMultipleSheets ? 3 : 2;
    if (activeStepKey === "importing") return hasMultipleSheets ? 4 : 3;
    if (activeStepKey === "result") return hasMultipleSheets ? 5 : 4;
    return 0;
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    if (onClose) onClose();
  };

  const resetState = () => {
    setActiveStepKey("file");
    setFile(null);
    setObtenidoDe("Meta Ads");
    setInspecting(false);
    setInspectData(null);
    setSelectedSheet(null);
    setMapping({});
    setValidationError(null);
    setPreviewing(false);
    setPreviewData(null);
    setSubmitting(false);
    submittingRef.current = false;
    setError(null);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError(null);
    setInspectData(null);
    setSelectedSheet(null);
    setMapping({});
    setPreviewData(null);
  };

  // Paso 1 -> Inspeccionar archivo (obtiene headers, hojas y mapeo sugerido)
  const handleInspectFile = async (sheetOverride = null) => {
    if (!file) {
      setError("Por favor seleccioná un archivo (.csv o .xlsx).");
      return;
    }

    try {
      setInspecting(true);
      setError(null);

      const formData = new FormData();
      formData.append("file", file);
      if (sheetOverride || selectedSheet) {
        formData.append("sheet_name", sheetOverride || selectedSheet);
      }

      const res = await apiFetch("/api/clientes/import/inspect", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let detail = "Error al inspeccionar el archivo.";
        try {
          const err = await res.json();
          if (err?.detail) detail = err.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const data = await res.json();
      setInspectData(data);

      if (data.sheets && data.sheets.length > 1 && !sheetOverride && !selectedSheet) {
        // Múltiples hojas: ir al paso de selección de hoja
        setActiveStepKey("sheet");
      } else {
        // Inicializar mapeo con sugerencia del backend
        setSelectedSheet(data.selected_sheet || sheetOverride || null);
        const suggested = data.suggested_mapping || {};
        // Si no se había tocado el mapeo o si se cambió de hoja, usar sugerencia nueva
        setMapping(suggested);
        setActiveStepKey("columns");
      }
    } catch (err) {
      setError(err?.message || "Error al leer el archivo.");
    } finally {
      setInspecting(false);
    }
  };

  // Paso de selección de hoja -> Confirmar hoja y avanzar a columnas (reseteando mapeo previo)
  const handleSheetSelect = async (sheetName) => {
    setSelectedSheet(sheetName);
    setMapping({});
    setPreviewData(null);
    setValidationError(null);
    setError(null);
    await handleInspectFile(sheetName);
  };

  // Paso de Columnas -> Generar vista previa (dry-run)
  const handleGeneratePreview = async () => {
    setValidationError(null);

    // 1. Validar campos obligatorios
    const missingRequired = REQUIRED_FIELDS.filter(
      (req) => !mapping[req] || String(mapping[req]).trim() === ""
    );
    if (missingRequired.length > 0) {
      setValidationError("Todos los campos obligatorios (Nombre, Teléfono, Vehículo) deben ser mapeados.");
      return;
    }

    // 2. Validar que no haya columnas duplicadas
    const used = {};
    for (const [sysField, colName] of Object.entries(mapping)) {
      if (colName && String(colName).trim() !== "") {
        const col = String(colName).trim();
        if (used[col]) {
          setValidationError(`La columna "${col}" no puede estar asignada a más de un campo (${used[col]} y ${sysField}).`);
          return;
        }
        used[col] = sysField;
      }
    }

    try {
      setPreviewing(true);
      setError(null);

      const formData = new FormData();
      formData.append("file", file);
      if (selectedSheet) formData.append("sheet_name", selectedSheet);
      formData.append("mapping", JSON.stringify(mapping));
      if (obtenidoDe) formData.append("obtenido_de", obtenidoDe);

      const res = await apiFetch("/api/clientes/import/preview", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let detail = "Error al generar la vista previa.";
        try {
          const err = await res.json();
          if (err?.detail) detail = err.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const data = await res.json();
      setPreviewData(data);
      setActiveStepKey("preview");
    } catch (err) {
      setError(err?.message || "Error al realizar la vista previa.");
    } finally {
      setPreviewing(false);
    }
  };

  // Paso final -> Confirmar e importar de forma persistente con cerrojo síncrono contra doble clic
  const handleConfirmImport = async () => {
    if (!file || submittingRef.current) return;
    if (!previewData?.preview_hash) {
      setError("La vista previa venció o no es válida. Generala nuevamente antes de importar.");
      return;
    }
    submittingRef.current = true;

    try {
      setSubmitting(true);
      setActiveStepKey("importing");
      setError(null);

      const formData = new FormData();
      formData.append("file", file);
      if (selectedSheet) formData.append("sheet_name", selectedSheet);
      formData.append("mapping", JSON.stringify(mapping));
      formData.append("obtenido_de", obtenidoDe || "Meta Ads");
      formData.append("preview_hash", previewData.preview_hash);

      const res = await apiFetch("/api/clientes/import-csv", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let detail = "Error al importar clientes.";
        try {
          const err = await res.json();
          if (err?.detail) detail = err.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const data = await res.json();
      setImportResult(data);
      setActiveStepKey("result");
      setResultDialogOpen(true);
    } catch (err) {
      setError(err?.message || "Error al importar clientes.");
      setActiveStepKey("preview");
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  // Navegación hacia atrás
  const handleBack = () => {
    setError(null);
    setValidationError(null);

    if (activeStepKey === "preview") {
      setActiveStepKey("columns");
    } else if (activeStepKey === "columns") {
      if (hasMultipleSheets) {
        setActiveStepKey("sheet");
      } else {
        setActiveStepKey("file");
      }
    } else if (activeStepKey === "sheet") {
      setActiveStepKey("file");
    }
  };

  const handleDuplicatesClose = (summary) => {
    setResultDialogOpen(false);
    setImportResult(null);
    resetState();
    if (onClose) onClose(summary);
  };

  const handleMappingFieldChange = (fieldKey, columnName) => {
    setValidationError(null);
    setPreviewData(null);
    setMapping((prev) => ({
      ...prev,
      [fieldKey]: columnName || null,
    }));
  };

  const isXlsx = file?.name?.toLowerCase().endsWith(".xlsx");
  const fileSizeKB = file ? Math.round(file.size / 1024) : 0;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ p: 2, pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, fontSize: "1.2rem" }}>
              Asistente de Importación de Clientes (.csv / .xlsx)
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              size="small"
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {loading && <LinearProgress />}

        <DialogContent sx={{ position: "relative", pt: 1 }}>
          <Backdrop
            open={loading}
            sx={{
              position: "absolute",
              zIndex: (t) => t.zIndex.drawer + 1,
              color: "#fff",
            }}
            invisible={!loading}
          >
            {loading && <CircularProgress />}
          </Backdrop>

          {/* Stepper visual */}
          <Stepper activeStep={getStepIndex()} sx={{ mb: 3, pt: 1 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* PASO 0: Archivo */}
          {activeStepKey === "file" && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Seleccioná un archivo <strong>.csv</strong> o <strong>.xlsx</strong> (Excel) exportado de tu CRM
                (Meta Ads, HubSpot, etc.).
              </Typography>

              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  "& .MuiAlert-message": {
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  },
                }}
              >
                <strong>Máximo recomendado: 250 clientes por archivo.</strong>
                <br />
                <Typography variant="caption" component="span" sx={{ color: "text.secondary" }}>
                  Archivos más grandes pueden causar lentitud o errores de conexión.
                </Typography>
              </Alert>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading}
                >
                  {file ? "Cambiar archivo" : "Seleccionar archivo (.csv / .xlsx)"}
                  <input
                    type="file"
                    accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>

                {file && (
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Formato: {isXlsx ? "Excel (.xlsx)" : "CSV (.csv)"} | Tamaño: {fileSizeKB} KB
                    </Typography>
                  </Box>
                )}
              </Box>

              <TextField
                select
                fullWidth
                label="Origen predeterminado (Obtenido de)"
                value={obtenidoDe}
                onChange={(e) => setObtenidoDe(e.target.value)}
                disabled={loading}
                helperText="Se utilizará este origen si la columna 'Origen' no está presente o viene vacía en el archivo."
                sx={{ mt: 1 }}
              >
                {OBTENIDO_LIST.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {activeStepKey !== "file" && file && (
            <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="body2">
                <strong>Archivo:</strong> {file.name} ({isXlsx ? "Excel .xlsx" : "CSV .csv"})
                {selectedSheet ? <> | <strong>Hoja:</strong> {selectedSheet}</> : null}
              </Typography>
            </Alert>
          )}

          {/* PASO 1: Hoja (Condicional para Excel multi-hoja) */}
          {activeStepKey === "sheet" && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Selección de Hoja
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tu archivo Excel contiene múltiples hojas. Seleccioná la hoja que contiene los datos a importar.
              </Typography>

              <TextField
                select
                fullWidth
                label="Hoja de cálculo"
                value={selectedSheet || ""}
                onChange={(e) => handleSheetSelect(e.target.value)}
                disabled={loading}
              >
                {(inspectData?.sheets || []).map((sh) => (
                  <MenuItem key={sh} value={sh}>
                    {sh}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {/* PASO 2: Columnas (Mapeo) */}
          {activeStepKey === "columns" && (
            <CsvColumnMappingStep
              headers={inspectData?.headers || []}
              mapping={mapping}
              onMappingChange={handleMappingFieldChange}
              ambiguousFields={inspectData?.ambiguous_fields || []}
              sampleRows={inspectData?.sample_rows || []}
              validationError={validationError}
            />
          )}

          {/* PASO 3: Vista previa (Dry-Run) */}
          {activeStepKey === "preview" && (
            <CsvPreviewStep
              previewData={previewData}
              loading={previewing}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2, pt: 1, justifyContent: "space-between" }}>
          <Box>
            {activeStepKey !== "file" && (
              <Button
                onClick={handleBack}
                disabled={loading}
                startIcon={<ArrowBackIcon />}
              >
                Atrás
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>

            {activeStepKey === "file" && (
              <Button
                onClick={() => handleInspectFile()}
                variant="contained"
                disabled={loading || !file}
                endIcon={<ArrowForwardIcon />}
              >
                Continuar
              </Button>
            )}

            {activeStepKey === "sheet" && (
              <Button
                onClick={() => handleInspectFile(selectedSheet)}
                variant="contained"
                disabled={loading || !selectedSheet}
                endIcon={<ArrowForwardIcon />}
              >
                Continuar a Mapeo
              </Button>
            )}

            {activeStepKey === "columns" && (
              <Button
                onClick={handleGeneratePreview}
                variant="contained"
                disabled={loading}
                endIcon={<ArrowForwardIcon />}
              >
                Generar Vista Previa
              </Button>
            )}

            {activeStepKey === "preview" && (
              <Button
                onClick={handleConfirmImport}
                variant="contained"
                color="primary"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
              >
                {submitting ? "Importando..." : "Confirmar e Importar"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <CsvDuplicatesDialog
        open={resultDialogOpen}
        onClose={handleDuplicatesClose}
        importResult={importResult}
        onRefresh={onImported}
      />
    </>
  );
}
